import * as Form from "@radix-ui/react-form"
import { ReactComponent as CheckboxSVG } from "images/global/Checkbox.svg"
import { eases } from "library/eases"
import { fresponsive } from "library/fullyResponsive"
import { generateGradientBorder } from "library/generateGradientBorder"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"

export default function Checkbox({
	name,
	children,
	required,
}: {
	name: string
	required?: boolean
	children: React.ReactNode
}) {
	return (
		<Form.Field name={name}>
			<Wrapper>
				<CheckWrap>
					<Control type="checkbox" required={required} />
					<Check />
				</CheckWrap>
				<Label>{children}</Label>
			</Wrapper>
		</Form.Field>
	)
}

const Wrapper = styled(Form.Label)`
	${fresponsive(css`
		padding-left: 24px;
		display: grid;
		grid-template-columns: auto 1fr;
		gap: 12px;
	`)}
`

const CheckWrap = styled.div`
	${fresponsive(css`
		border-radius: 6px;
		background: linear-gradient(
			228deg,
			rgb(36 36 36 / 25%) -27%,
			rgb(138 138 138 / 25%) 192.85%
		);
		backdrop-filter: blur(2px);
		width: 18px;
		height: 18px;
		display: grid;
		place-items: center;
		outline: 1px solid transparent;
		transition: outline-color 0.2s ${eases.quad.out};
		cursor: pointer;
		position: relative;
		overflow: clip;
	`)}

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		${generateGradientBorder(
			"linear-gradient(17deg, #252525 16.5%, #8B8B8B 218.45%)",
			0.5,
		)};
		transition: opacity 0.2s ${eases.quad.out};
	}

	&:hover {
		outline-color: rgb(7 126 158 / 75%);

		&::before {
			opacity: 0;
		}
	}

	div[data-invalid] & {
		outline-color: #ff5151;
	}

	input:checked + & {
		background: ${colors.teal};
	}
`

const Check = styled(CheckboxSVG)`
	${fresponsive(css`
		width: 18px;
		height: 18px;
		transition:
			opacity 0.2s ${eases.quad.out},
			translate 0.2s ${eases.quad.out};
		translate: 0 110%;
		opacity: 0;
	`)}
`

const Control = styled(Form.Control)`
	&:checked + svg {
		opacity: 1;
		translate: 0 0;
	}

	&::before {
		content: "";
		position: absolute;
		background: transparent;
		transition: background 0.2s ${eases.quad.out};
		z-index: -1;
		${fresponsive(css`
			inset: 0.5px;
			border-radius: 5.5px;
		`)}
	}

	&:checked::before {
		background: ${colors.teal};
	}
`

const Label = styled.div`
	${textStyles.body2S};
	color: ${colors.gray700};
	transition: color 0.2s ${eases.quad.out};

	a {
		color: ${colors.orange500};
		font-family: inherit;
	}

	div[data-invalid] & {
		color: #ff5151;
	}
`
